import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Briefcase, PlusCircle, ListChecks, PieChart, FileText } from 'lucide-react';
import LeaveRequestForm from '../leave/LeaveRequestForm';
import LeaveRequestStatus from '../leave/LeaveRequestStatus';
import LeaveTransactionModal from '../leave/LeaveTransactionModal';
import { usePageHeader } from '../../contexts/PageHeaderContext';
import { leaveService, positionService } from '../../api/services';
import { getActiveFte, getAdjustedBalance } from '../../utils/leaveBalance';
import useEmployeeProfile from '../../hooks/useEmployeeProfile';

const EmployeeLeaveManagement = () => {
  const { setHeader } = usePageHeader();
  const userProfile = useEmployeeProfile();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('new');
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [leaveTypeUnitMap, setLeaveTypeUnitMap] = useState({});
  const [fte, setFte] = useState(1);
  const [isLoadingBalances, setIsLoadingBalances] = useState(false);
  const [selectedLeaveType, setSelectedLeaveType] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTransactions, setModalTransactions] = useState([]);
  const [isLoadingModal, setIsLoadingModal] = useState(false);

  // Check if navigated from leave request submission
  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }
  }, [location.state]);

  // Fetch transactions with optional filters
  const fetchTransactions = async (leaveTypeId, filterOptions = {}) => {
    if (!userProfile?.employeeNo) return;

    const { startDate = '', endDate = '', transactionType = '' } = filterOptions;

    setIsLoadingModal(true);
    try {
      console.log('📊 Fetching transaction breakdown for:', leaveTypeId, {
        dateRange: startDate || endDate ? `${startDate || 'any'} to ${endDate || 'any'}` : 'all',
        type: transactionType || 'all'
      });

      // Build additional filter params
      let additionalParams = { $orderby: 'TransactionDate desc' };

      // Build filters array
      let filterConditions = [];

      // Date filter
      if (startDate && endDate) {
        const startDateISO = `${startDate}T00:00:00Z`;
        const endDateISO = `${endDate}T23:59:59Z`;
        filterConditions.push(`TransactionDate ge ${startDateISO} and TransactionDate le ${endDateISO}`);
      } else if (startDate) {
        const startDateISO = `${startDate}T00:00:00Z`;
        filterConditions.push(`TransactionDate ge ${startDateISO}`);
      } else if (endDate) {
        const endDateISO = `${endDate}T23:59:59Z`;
        filterConditions.push(`TransactionDate le ${endDateISO}`);
      }

      // Transaction type filter (Dynamics enum format)
      if (transactionType) {
        filterConditions.push(`TransactionType eq Microsoft.Dynamics.DataEntities.LeaveTransactionType'${transactionType}'`);
      }

      // Combine all filters
      if (filterConditions.length > 0) {
        additionalParams.$filter = filterConditions.join(' and ');
      }

      const response = await leaveService.getLeaveBankTransactions(
        userProfile.employeeNo,
        leaveTypeId,
        additionalParams
      );
      setModalTransactions(response.data.value || []);
      console.log('✅ Transaction breakdown fetched:', response.data.value?.length || 0, 'records');
    } catch (error) {
      console.error('❌ Failed to fetch transaction breakdown:', error);
      setModalTransactions([]);
    } finally {
      setIsLoadingModal(false);
    }
  };

  // Open modal and fetch transactions
  const openTransactionModal = async (leaveTypeId) => {
    setSelectedLeaveType(leaveTypeId);
    setIsModalOpen(true);
    setModalTransactions([]);
    await fetchTransactions(leaveTypeId);
  };

  // Handle filter change from modal
  const handleFilterChange = async (filters) => {
    if (selectedLeaveType) {
      await fetchTransactions(selectedLeaveType, filters);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedLeaveType(null);
    setModalTransactions([]);
  };

  useEffect(() => {
    setHeader({
      icon: Briefcase,
      title: 'My Leave Details',
      subtitle: 'Create new leave requests, track their status, and view your remaining leave balance. (If you want to cancel a leave request that your manager has approved please email them and ask them to email payroll@shp.org.uk to cancel the leave)',
    });
    return () => setHeader({ icon: null, title: '', subtitle: '' });
  }, [setHeader]);

  // Fetch leave balances and leave types for unit mapping
  useEffect(() => {
    const fetchLeaveBalances = async () => {
      if (!userProfile?.employeeNo) {
        console.log('⏳ Waiting for employee number...');
        return;
      }

      console.log('📊 Fetching leave balances for employee:', userProfile.employeeNo);
      setIsLoadingBalances(true);
      try {
        // Fetch leave types for unit mapping
        try {
          const leaveTypesResponse = await leaveService.getLeaveTypes();
          const allLeaveTypes = leaveTypesResponse.data?.value || [];
          // Build map: Type -> LeaveAmountUnit
          const unitMap = allLeaveTypes.reduce((acc, lt) => {
            acc[lt.Type] = lt.LeaveAmountUnit;
            return acc;
          }, {});
          setLeaveTypeUnitMap(unitMap);
          console.log('✅ Leave type unit map built:', Object.keys(unitMap).length, 'types');
        } catch (leaveTypeError) {
          console.warn('⚠️ Failed to fetch leave types for unit mapping:', leaveTypeError);
        }

        // FTE from the worker's active position(s); defaults to 1 if unavailable
        try {
          const positionsResponse = await positionService.getPositionsByWorker(userProfile.employeeNo);
          setFte(getActiveFte(positionsResponse.data?.value || []));
        } catch (positionError) {
          console.warn('⚠️ Failed to fetch positions for FTE, using 1:', positionError);
          setFte(1);
        }

        const response = await leaveService.getLeaveBalancesByEmployee(userProfile.employeeNo);
        console.log('✅ Leave balances fetched:', response.data.value?.length || 0, 'records');
        setLeaveBalances(response.data.value || []);
      } catch (error) {
        console.error('❌ Failed to fetch leave balances:', error);
        setLeaveBalances([]);
      } finally {
        setIsLoadingBalances(false);
      }
    };

    if (activeTab === 'balance' && !userProfile.isLoading && userProfile.employeeNo) {
      fetchLeaveBalances();
    }
  }, [activeTab, userProfile.isLoading, userProfile.employeeNo]); // Only depend on specific values, not the whole object

  const tabs = [
    { id: 'new', label: 'New Leave Request', icon: PlusCircle },
    { id: 'history', label: 'Leave Request History', icon: ListChecks },
    { id: 'balance', label: 'Leave Balance View', icon: PieChart },
  ];

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
      <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 lg:p-8">
        <div className="border-b border-gray-200 mb-4 sm:mb-6">
          <nav className="-mb-px flex space-x-2 sm:space-x-4 overflow-x-auto" aria-label="Tabs">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                  className={`whitespace-nowrap py-2 sm:py-3 px-2 sm:px-4 border-b-2 text-xs sm:text-sm font-medium flex items-center gap-1.5 sm:gap-2 ${
                  isActive
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {activeTab === 'new' && (
          <LeaveRequestForm hideCard={true} />
      )}

      {activeTab === 'history' && (
          <LeaveRequestStatus hideCard={true} />
      )}

      {activeTab === 'balance' && (
          <div>
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <PieChart className="w-5 h-5 text-indigo-600" />
            Leave Balance
          </h2>

          {userProfile.isLoading || isLoadingBalances ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
              <p className="ml-3 text-gray-600">
                {userProfile.isLoading ? 'Loading employee profile...' : 'Loading leave balances...'}
              </p>
            </div>
          ) : leaveBalances.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <PieChart className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">No leave balance data available</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {leaveBalances.map((balance, index) => {
                const leaveAmountUnit = leaveTypeUnitMap[balance.LeaveTypeId] || 'Days';
                const remaining = balance.BalanceAvailable != null ? balance.BalanceAvailable : 'N/A';
                const unit = leaveAmountUnit === 'Hours' ? 'hours' : 'days';
                const balanceType = balance.LeaveTypeId || 'N/A';
                // Accrual Start = FullTimeEquivalent x BalanceAvailable
                const accrualStart = getAdjustedBalance(balance.BalanceAvailable, fte);

                // Determine color based on balance type
                const getBalanceColor = (type) => {
                  const lowerType = type.toLowerCase();
                  if (lowerType.includes('pto')) return 'indigo';
                  if (lowerType.includes('vacation')) return 'blue';
                  if (lowerType.includes('sick')) return 'green';
                  if (lowerType.includes('bereavement')) return 'gray';
                  return 'purple';
                };

                const color = getBalanceColor(balanceType);
                const colorClasses = {
                  indigo: 'border-indigo-200 bg-indigo-50',
                  blue: 'border-blue-200 bg-blue-50',
                  green: 'border-green-200 bg-green-50',
                  gray: 'border-gray-200 bg-gray-50',
                  purple: 'border-purple-200 bg-purple-50',
                };

                const textColorClasses = {
                  indigo: 'text-indigo-700',
                  blue: 'text-blue-700',
                  green: 'text-green-700',
                  gray: 'text-gray-700',
                  purple: 'text-purple-700',
                };

                return (
                  <div
                    key={index}
                    className={`border-2 rounded-lg p-5 hover:shadow-lg transition-shadow ${colorClasses[color]}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-lg font-bold text-gray-800">{balanceType}</h3>
                    </div>

                    <div className="mb-4">
                      <div className={`text-4xl font-bold ${textColorClasses[color]}`}>
                        {remaining !== 'N/A' ? Number(remaining).toFixed(2) : 'N/A'}
                      </div>
                      <div className="text-xs text-gray-600 mt-1">
                        {unit} remaining (not including future leave booked)
                        <span className="block text-gray-400 mt-0.5">(As of date: {new Date().toLocaleDateString('en-GB')})</span>
                      </div>
                    </div>

                    <div className="pt-3 border-t border-gray-300">
                      <div className="text-xs text-gray-600">
                        <span className="font-medium">Accrual Start:</span> {accrualStart != null ? `${accrualStart.toFixed(2)} ${unit}` : 'N/A'}
                      </div>
                      <div className="text-xs text-gray-600 mt-1">
                        <span className="font-medium">Employee:</span> {userProfile.displayName || 'N/A'}
                      </div>
                    </div>

                    <p className="text-xs font-medium text-indigo-600 mt-3 mb-1 border-l-2 border-indigo-400 pl-2">To view the future remaining days, click below:</p>
                    {/* View Breakdown Button */}
                    <button
                      onClick={() => openTransactionModal(balance.LeaveTypeId)}
                      className="mt-1 w-full flex items-center justify-center gap-2 py-2 px-3 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md transition-colors"
                    >
                      <FileText className="w-4 h-4" />
                      View Breakdown
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      </div>

      {/* Transaction Breakdown Modal */}
      <LeaveTransactionModal
        isOpen={isModalOpen}
        onClose={closeModal}
        leaveTypeId={selectedLeaveType}
        transactions={modalTransactions}
        isLoading={isLoadingModal}
        onFilterChange={handleFilterChange}
      />
    </div>
  );
};

export default EmployeeLeaveManagement;


